/**
 * A CMMD handoff link's `task` search parameter
 * (`{FORGE_WEB_URL}/projects/{cmmdProjectId}?task={taskId}`).
 *
 * TanStack Router's default search parser JSON-parses every value that looks
 * like a number, so a numeric CMMD task id such as `?task=412` arrives at
 * `validateSearch` as the number `412`, not the string `"412"`. A plain
 * `typeof value === "string"` check drops it silently, and the handoff link
 * never reaches the page that would open it. Accept both shapes and
 * normalize to a string; anything else (an object, `NaN`, `Infinity`) is not
 * a task id and is dropped.
 */
export function parseTaskSearchParam(value: unknown): string | undefined {
  if (typeof value === "string") {
    // `?task=` (or an all-whitespace value) parses as an empty string, which
    // is not a task id. Treat it as absent rather than passing it through:
    // an empty string is truthy-adjacent enough to fool one caller
    // (`CmmdProjectHome`'s truthy check) while `Number("")` is `0`, which
    // fools another (`useProjectRequestedTaskLaunch`, which checks only for
    // `null`) into starting a doomed lookup for task `0`.
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return undefined;
}
